import React from "react";
import { useParams } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { FiDownload, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const GAMES = require("../data/games.json");
import { MINIO_URL } from "../config";

function GameDetail() {
    const { id } = useParams();
    const game = Object.entries(GAMES).filter(([key, value]) => value.id == id)[0][1];
    const [active, setActive] = useState(0);
    const mediaRef = useRef(null);
    const thumbnailScrollRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [imageAspectRatios, setImageAspectRatios] = useState({});

    if (!game) {
        return (
            <div className="pt-24 px-4">
                <div className="max-w-4xl mx-auto py-16 text-center">Game not found</div>
            </div>
        );
    }

    const screenshots = game.screenshots ? game.screenshots.split(',') : [game.thumb];

    // Function to detect if image is portrait
    const handleImageLoad = (index, e) => {
        const img = e.target;
        const isPortrait = img.naturalHeight > img.naturalWidth;
        setImageAspectRatios(prev => ({
            ...prev,
            [index]: isPortrait
        }));
    };

    // Check scroll position for arrow visibility
    const checkScrollPosition = () => {
        if (!thumbnailScrollRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = thumbnailScrollRef.current;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    };

    useEffect(() => {
        checkScrollPosition();
        const scrollContainer = thumbnailScrollRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', checkScrollPosition);
            window.addEventListener('resize', checkScrollPosition);
        }

        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', checkScrollPosition);
            }
            window.removeEventListener('resize', checkScrollPosition);
        };
    }, [screenshots]);

    // Global mouse listeners for dragging
    useEffect(() => {
        const handleGlobalMouseUp = () => {
            setIsDragging(false);
        };

        const handleGlobalMouseMove = (e) => {
            if (!isDragging || !thumbnailScrollRef.current) return;
            e.preventDefault();
            const rect = thumbnailScrollRef.current.getBoundingClientRect();
            const x = e.pageX - rect.left;
            const walk = (x - startX) * 2;
            thumbnailScrollRef.current.scrollLeft = scrollLeft - walk;
        };

        if (isDragging) {
            document.addEventListener('mouseup', handleGlobalMouseUp);
            document.addEventListener('mousemove', handleGlobalMouseMove);
            return () => {
                document.removeEventListener('mouseup', handleGlobalMouseUp);
                document.removeEventListener('mousemove', handleGlobalMouseMove);
            };
        }
    }, [isDragging, startX, scrollLeft]);

    // Mouse drag handlers
    const handleMouseDown = (e) => {
        if (!thumbnailScrollRef.current) return;
        setIsDragging(true);
        const rect = thumbnailScrollRef.current.getBoundingClientRect();
        setStartX(e.pageX - rect.left);
        setScrollLeft(thumbnailScrollRef.current.scrollLeft);
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !thumbnailScrollRef.current) return;
        e.preventDefault();
        const rect = thumbnailScrollRef.current.getBoundingClientRect();
        const x = e.pageX - rect.left;
        const walk = (x - startX) * 2; // Scroll speed
        thumbnailScrollRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    // Navigation handlers
    const scrollLeftHandler = () => {
        if (!thumbnailScrollRef.current) return;
        const scrollAmount = thumbnailScrollRef.current.clientWidth * 0.8;
        thumbnailScrollRef.current.scrollBy({
            left: -scrollAmount,
            behavior: 'smooth'
        });
    };

    const scrollRightHandler = () => {
        if (!thumbnailScrollRef.current) return;
        const scrollAmount = thumbnailScrollRef.current.clientWidth * 0.8;
        thumbnailScrollRef.current.scrollBy({
            left: scrollAmount,
            behavior: 'smooth'
        });
    };

    const openFullscreen = () => {
        const el = mediaRef.current;
        if (!el) return;

        // Prefer native video fullscreen on iOS Safari
        const anyEl = el;
        const isVideo = el.tagName === 'VIDEO';

        try {
            if (isVideo && typeof anyEl.webkitEnterFullscreen === 'function') {
                anyEl.webkitEnterFullscreen();
                return;
            }
            if (typeof anyEl.requestFullscreen === 'function') {
                anyEl.requestFullscreen().catch(() => { });
                return;
            }
            if (typeof anyEl.webkitRequestFullscreen === 'function') {
                anyEl.webkitRequestFullscreen();
                return;
            }
        } catch (_) {
            // fall through to overlay fallback
        }

        // Fallback: lightweight overlay that mimics fullscreen (works on older iOS)
        const src = isVideo ? anyEl.currentSrc || anyEl.src : anyEl.currentSrc || anyEl.src;
        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.inset = '0';
        overlay.style.background = 'rgba(0,0,0,0.95)';
        overlay.style.zIndex = '9999';
        overlay.style.display = 'flex';
        overlay.style.alignItems = 'center';
        overlay.style.justifyContent = 'center';
        overlay.style.touchAction = 'manipulation';

        if (isVideo) {
            const v = document.createElement('video');
            v.src = src;
            v.controls = true;
            v.playsInline = true;
            v.style.maxWidth = '100%';
            v.style.maxHeight = '100%';
            v.autoplay = true;
            overlay.appendChild(v);
        } else {
            const img = document.createElement('img');
            img.src = src;
            img.style.maxWidth = '100%';
            img.style.maxHeight = '100%';
            img.style.objectFit = 'contain';
            img.style.width = 'auto';
            img.style.height = 'auto';
            // Detect if image is portrait and adjust accordingly
            img.onload = function() {
                const isPortrait = img.naturalHeight > img.naturalWidth;
                if (isPortrait) {
                    img.style.maxHeight = '90vh';
                    img.style.maxWidth = '90vw';
                } else {
                    img.style.maxWidth = '90vw';
                    img.style.maxHeight = '90vh';
                }
            };
            overlay.appendChild(img);
        }

        const close = () => {
            overlay.removeEventListener('click', close);
            document.body.removeChild(overlay);
        };
        overlay.addEventListener('click', close, { passive: true });
        document.body.appendChild(overlay);
    };

    return (
        <div className="pt-20 pb-16 min-h-screen">
            <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
                <div className="grid md:grid-cols-3 gap-3 sm:gap-4 md:gap-8">
                    {/* Summary */}
                    <div className="col-span-1 md:order-1 bg-gradient-to-br from-yellow-600 to-yellow-900 p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl shadow overflow-hidden">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <img src="/semperor.png" className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 object-contain rounded flex-shrink-0" alt="logo" />
                            <div className="min-w-0 flex-1">
                                <div className="font-bold text-sm sm:text-base md:text-lg truncate">{game.name}</div>
                                <div className="text-xs md:text-sm text-black truncate">{game.category}</div>
                                <div className="mt-1 sm:mt-2 text-yellow-400 font-semibold text-xs sm:text-sm md:text-base">{game.rating} ★</div>
                            </div>
                        </div>

                        <div className="mt-3 sm:mt-4 md:mt-6 flex flex-col gap-2">
                            <div className="flex items-center justify-center opacity-50">
                                <FiDownload /> <img src="/images/google-play.png" className="object-contain ml-4 w-40" alt="Google play store" title="Google play store" />
                            </div>
                            <div className="mt-3 sm:mt-4 md:mt-6 flex flex-col gap-2">
                            <div className="flex items-center justify-center opacity-50">
                                    <FiDownload /> <img src="/images/ios.png" className="object-contain ml-4 w-40" alt="IOS store" title="IOS store" />
                                </div>
                            </div>
                        </div>

                        <div className="mt-3 sm:mt-4 md:mt-6 text-xs md:text-sm text-black space-y-1">
                            <div className="truncate"><strong>Size:</strong> <span className="whitespace-nowrap">52 MB</span></div>
                            <div className="truncate"><strong>Version:</strong> <span className="whitespace-nowrap">1.2.3</span></div>
                            <div className="truncate"><strong>Updated:</strong> <span className="whitespace-nowrap">2025-01-18</span></div>
                        </div>
                    </div>

                    {/* Slider */}
                    <div className="md:col-span-2 md:order-2 min-w-0">
                        <div className="bg-gradient-to-br from-yellow-600 to-yellow-900 p-2 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl shadow overflow-hidden">
                            <div className="relative w-full h-48 sm:h-64 md:h-80 lg:h-96 flex items-center justify-center bg-gray-900/30 rounded overflow-hidden">
                                {/* main media */}
                                {/\.mp4($|\?)/i.test(screenshots[active]) ? (
                                    <video
                                        ref={mediaRef}
                                        controls
                                        className="w-full h-full object-cover rounded"
                                    >
                                        <source src={`${MINIO_URL}${game.id}/${screenshots[active]}`} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                ) : (
                                    <>
                                        <img
                                            ref={mediaRef}
                                            src={`${MINIO_URL}${game.id}/${screenshots[active] || game.thumb}`}
                                            alt="screenshot"
                                            onLoad={(e) => handleImageLoad(active, e)}
                                            className={`w-full h-full rounded pointer-events-none bg ${
                                                imageAspectRatios[active] ? 'object-contain' : 'object-cover'
                                            }`}
                                        />
                                        <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 md:top-3 md:right-3 z-20 pointer-events-auto">
                                            <button
                                                onClick={openFullscreen}
                                                onTouchEnd={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openFullscreen();
                                                }}
                                                className="bg-yellow-500/30 hover:bg-black/90 active:bg-black backdrop-blur-sm px-2.5 py-2 sm:px-3 sm:py-2 md:px-4 md:py-2.5 text-base sm:text-lg md:text-xl rounded-md transition-all touch-manipulation"
                                                style={{
                                                    touchAction: 'manipulation',
                                                    WebkitTapHighlightColor: 'transparent'
                                                }}
                                                aria-label="Fullscreen"
                                            >
                                                &#x26F6;
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* thumbnails */}
                            <div className="mt-2 sm:mt-2 md:mt-3 relative">
                                {/* Left arrow */}
                                {showLeftArrow && (
                                    <button
                                        onClick={scrollLeftHandler}
                                        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 items-center justify-center bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full border border-white/20 text-black transition-all hover:scale-110 shadow-lg"
                                        aria-label="Scroll left"
                                    >
                                        <FiChevronLeft className="text-lg md:text-xl" />
                                    </button>
                                )}

                                {/* Right arrow */}
                                {showRightArrow && (
                                    <button
                                        onClick={scrollRightHandler}
                                        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 items-center justify-center bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full border border-white/20 text-black transition-all hover:scale-110 shadow-lg"
                                        aria-label="Scroll right"
                                    >
                                        <FiChevronRight className="text-lg md:text-xl" />
                                    </button>
                                )}

                                {/* Thumbnail container */}
                                <div
                                    ref={thumbnailScrollRef}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseLeave}
                                    className={`flex gap-1.5 sm:gap-2 md:gap-3 overflow-x-auto py-2 scrollbar-hide -mx-2 sm:-mx-1 md:-mx-0 px-2 sm:px-1 md:px-0 ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab md:cursor-grab'} md:scroll-smooth`}
                                    style={{
                                        userSelect: isDragging ? 'none' : 'auto',
                                        WebkitUserSelect: isDragging ? 'none' : 'auto'
                                    }}
                                >
                                    {screenshots.map((s, i) => {
                                        const isVideo = /\.mp4($|\?)/i.test(s);
                                        const thumbnailWidth = 'w-[70px] sm:w-[80px] md:w-[100px] lg:w-[120px]';
                                        const thumbnailHeight = 'h-14 sm:h-16 md:h-20';

                                        return (
                                            <button
                                                key={i}
                                                onClick={() => { setActive(i); }}
                                                className={`flex-shrink-0 ${thumbnailWidth} ${thumbnailHeight} rounded overflow-hidden border-2 ${i === active ? 'border-yellow-500 ring-1 sm:ring-2 ring-yellow-500/50' : 'border-transparent hover:border-yellow-300/50'} transition-all relative bg-gray-800`}
                                            >
                                                {isVideo ? (
                                                    <div className="relative w-full h-full bg-gray-900 overflow-hidden">
                                                        <video
                                                            src={`${MINIO_URL}${game.id}/${s}`}
                                                            className="w-full h-full object-cover absolute inset-0"
                                                            preload="metadata"
                                                            muted
                                                            playsInline
                                                            onLoadedMetadata={(e) => {
                                                                const video = e.target;
                                                                if (video.duration > 0) {
                                                                    video.currentTime = 0.1;
                                                                }
                                                            }}
                                                            onSeeked={(e) => {
                                                                e.target.style.opacity = '1';
                                                            }}
                                                            onError={(e) => {
                                                                // Fallback if video fails to load
                                                                e.target.style.opacity = '1';
                                                            }}
                                                            style={{ opacity: 0 }}
                                                        >
                                                            <source src={`${MINIO_URL}${game.id}/${s}`} type="video/mp4" />
                                                        </video>
                                                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none z-10">
                                                            <span className="text-black text-base sm:text-lg md:text-xl drop-shadow-lg">▶</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={`${MINIO_URL}${game.id}/${s}`}
                                                        onLoad={(e) => handleImageLoad(i, e)}
                                                        className={`w-full h-full ${
                                                            imageAspectRatios[i] ? 'object-contain bg-yellow-900/50' : 'object-cover'
                                                        }`}
                                                        alt={`shot-${i}`}
                                                        loading="lazy"
                                                    />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* info section */}
                <div className="mt-3 sm:mt-4 md:mt-6 bg-gradient-to-br from-yellow-600 to-yellow-900 p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl shadow overflow-hidden">
                    <div
                        className="text-black game-description"
                        dangerouslySetInnerHTML={{
                            __html: game.description
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
export default GameDetail;